import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { useState, useEffect } from "react"
import { getShopCustomerGroup } from "@/api/shop"
import { employeePlatformUserIdGroupNoChange, platformUserIdGroupNoChange } from "@/api/employee"
import { useShopStore } from "@/store/shop"
import { useAuthStore } from "@/store/auth"
import { toast } from 'sonner'
import type { CustomerGroup } from "@/types/shop"
import type { PlatformUserId } from "@/types/employee"

interface PlatformUserLinkDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  platformUserId: PlatformUserId | null
  onSuccess?: () => void
}

export function PlatformUserLinkDialog({ open, onOpenChange, platformUserId, onSuccess }: PlatformUserLinkDialogProps) {
  const [customerGroups, setCustomerGroups] = useState<CustomerGroup[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [groupNo, setGroupNo] = useState<string>("")
  const [isFixedGroup, setIsFixedGroup] = useState<string>("T")
  const [useLinked, setUseLinked] = useState<string>("true")

  const { selectedShop } = useShopStore()
  const { user } = useAuthStore()
  const isSuperAdmin = user?.isSuperAdmin === true
  const allowedEmployeeGroupNo = user?.allowedEmployeeGroupNo ?? null
  const allowedEmployeeIsFixedGroup = user?.allowedEmployeeIsFixedGroup

  const isCafe24User = user?.platform === 'cafe24'
  const shopNo = isCafe24User && selectedShop ? selectedShop.shopNo : 1

  // 다이얼로그 열릴 때 데이터 초기화
  useEffect(() => {
    if (open && platformUserId) {
      setGroupNo(platformUserId.groupNo.toString())
      setUseLinked(platformUserId.useLinked.toString())
      loadCustomerGroups()
    }
  }, [open, platformUserId])

  const loadCustomerGroups = async () => {
    try {
      setIsLoading(true)
      const response = await getShopCustomerGroup({ shopNo })
      
      if (response.code === 200 && response.data) {
        setCustomerGroups(response.data)
        if (!isSuperAdmin) {
          if (allowedEmployeeGroupNo !== null) {
            const allowedGroupExists = response.data.some(
              (group) => group.groupNo === allowedEmployeeGroupNo
            )
            setGroupNo(
              allowedGroupExists
                ? allowedEmployeeGroupNo.toString()
                : response.data[0]?.groupNo.toString() ?? ""
            )
          }
          if (typeof allowedEmployeeIsFixedGroup === "boolean") {
            setIsFixedGroup(allowedEmployeeIsFixedGroup ? "T" : "F")
          }
        }
      } else {
        toast.error("고객 등급 정보를 가져오는데 실패했습니다.")
      }
    } catch (error) {
      console.error("고객 등급 조회 오류:", error)
      toast.error("고객 등급 정보를 가져오는 중 오류가 발생했습니다.")
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!platformUserId) {
      toast.error("수정할 회원 정보가 없습니다.")
      return
    }

    if (!groupNo) {
      toast.error("회원 등급을 선택해주세요.")
      return
    }

    setIsLoading(true)
    
    try {
      // 먼저 employeePlatformUserIdGroupNoChange API 호출
      const employeeResponse = await employeePlatformUserIdGroupNoChange(platformUserId.id, {
        groupNo: parseInt(groupNo),
        isFixedGroup: isFixedGroup as "T" | "F"
      })

      if (employeeResponse.code !== 200) {
        toast.error(employeeResponse.message || "임직원 회원 등급 변경에 실패했습니다.")
        return
      }

      // 그 다음 platformUserIdGroupNoChange API 호출
      const platformResponse = await platformUserIdGroupNoChange(platformUserId.id, {
        useLinked: useLinked === "true"
      })

      if (platformResponse.code === 200) {
        toast.success("회원 연동 정보가 성공적으로 수정되었습니다.")
        onOpenChange(false)
        onSuccess?.()
      } else {
        toast.error(platformResponse.message || "회원 연동 수정에 실패했습니다.")
      }
    } catch (error) {
      console.error("회원 연동 수정 오류:", error)
      toast.error("회원 연동 수정 중 오류가 발생했습니다.")
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setGroupNo("")
    setIsFixedGroup("T")
    setUseLinked("true")
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>회원 연동 수정</DialogTitle>
            <DialogDescription>
              선택한 회원의 연동 정보를 수정합니다.
              {platformUserId && (
                <span className="block text-sm font-medium mt-2">
                  회원 ID: {platformUserId.platformUserId}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>회원 등급</Label>
              <Select
                value={groupNo}
                onValueChange={setGroupNo}
                disabled={isLoading || !isSuperAdmin}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={isLoading ? "로딩 중..." : "변경할 회원 등급 선택"} />
                </SelectTrigger>
                <SelectContent>
                  {customerGroups.map((group) => (
                    <SelectItem key={group.groupNo} value={group.groupNo.toString()}>
                      {group.groupName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label>회원 등급 고정 여부</Label>
              <Select
                value={isFixedGroup}
                onValueChange={setIsFixedGroup}
                disabled={isLoading || !isSuperAdmin}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="T">회원 등급 고정함</SelectItem>
                  <SelectItem value="F">회원 등급 고정안함</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label>연동 상태</Label>
              <RadioGroup
                value={useLinked}
                onValueChange={setUseLinked}
                className="flex flex-row gap-6"
                disabled={isLoading}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="true" id="linked" />
                  <Label htmlFor="linked" className="cursor-pointer">연동</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="false" id="unlinked" />
                  <Label htmlFor="unlinked" className="cursor-pointer">비연동</Label>
                </div>
              </RadioGroup>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              취소
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "수정 중..." : "수정"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
